const MEGABYTE = 1024 * 1024;

export const COMPRESSIBLE_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
];

// video/ogg is intentionally excluded: OGG video is almost always Theora, which
// WebCodecs cannot decode in Chrome/Safari. Including it would let an oversize
// .ogg pass canCompressFile, flash the "Comprimiendo…" toast + progress bar, then
// fail inside mediabunny — two toasts instead of an immediate rejection. MP4
// (H.264) and WebM (VP8/VP9/AV1) decode fine via WebCodecs. Oversize .ogg now
// falls straight through to the fileSizeExceeded rejection.
export const COMPRESSIBLE_VIDEO_TYPES = ['video/mp4', 'video/webm'];

// iPhone "High Efficiency" formats. Chrome/Firefox/Edge cannot decode HEIC/HEIF
// (Safari-only), so left as-is they render a broken/"expired" placeholder in
// chat and may fail to deliver. These are transcoded to JPEG before send
// regardless of size — a compatibility fix, not size compression. libheif (via
// the lazily-loaded heic-to chunk) is the only browser-side HEIC decoder.
export const TRANSCODE_TO_JPEG_IMAGE_TYPES = ['image/heic', 'image/heif'];

// JPEG quality for the HEIC→JPEG transcode. High enough to preserve photo
// detail; the size-compression ladder still runs afterward if the transcoded
// JPEG exceeds the channel limit.
export const HEIC_TRANSCODE_JPEG_QUALITY = 0.85;

// Upper bound on how long a single HEIC decode may run before we give up. The
// heic-to worker has no internal timeout: a pathological input or a killed
// worker never settles the decode promise, which would wedge the whole
// sequential send loop and leave the composer disabled. ~7.5s is the measured
// 12MP baseline, so this is generous headroom, not a tight budget.
export const HEIC_TRANSCODE_TIMEOUT_MS = 45_000;

// Inputs above these sizes are not worth decoding in the browser; they keep
// the previous hard-rejection behavior.
export const MAX_IMAGE_INPUT_BYTES = 80 * MEGABYTE;
export const MAX_VIDEO_INPUT_BYTES = 300 * MEGABYTE;

// The HEIC path needs its own, much tighter cap. The native path above decodes
// via createImageBitmap and releases the bitmap deterministically
// (`bitmap.close()`), but libheif runs in heic-to's wasm worker, whose linear
// memory is grow-only and whose worker is never terminated — so one oversized
// decode raises the tab's memory high-water mark for the rest of the session.
// 32MB is far above any real camera HEIC (a 12MP iPhone photo is ~2MB, a 48MP
// one ~5-8MB, a panorama ~15MB) while keeping the worst-case wasm heap bounded.
export const MAX_HEIC_INPUT_BYTES = 32 * MEGABYTE;
// Shown to the user when a HEIC exceeds the cap above. Keep in sync with it.
export const MAX_HEIC_INPUT_HUMAN = '32 MB';

// WhatsApp re-renders images well below this; capping the longest edge keeps
// canvas memory bounded for huge camera originals without visible quality loss.
export const IMAGE_MAX_DIMENSION = 4096;
// Attempt ladder: try full size at descending JPEG quality, then scale down.
// First encode that fits the byte limit wins.
export const IMAGE_SCALE_STEPS = [1, 0.8, 0.65, 0.5, 0.35];
export const IMAGE_QUALITY_STEPS = [0.85, 0.75, 0.62, 0.5];

// Fraction of the byte limit budgeted to the streams; the remainder absorbs
// MP4 container overhead and hardware-encoder bitrate overshoot.
export const VIDEO_SIZE_SAFETY_FACTOR = 0.92;
// If the first encode still overshoots the limit, retry once at this fraction
// of the computed bitrate.
export const VIDEO_RETRY_BITRATE_FACTOR = 0.7;
// Below this video bitrate the output is unwatchable — fail instead of
// producing garbage (a video this long cannot fit the channel limit).
export const MIN_VIDEO_BITRATE_BPS = 150_000;
export const TARGET_AUDIO_BITRATE_BPS = 96_000;
// Budget estimate when the source AAC track is passed through (no AAC encoder
// in this browser) and its real bitrate cannot be measured.
export const FALLBACK_AUDIO_BITRATE_BPS = 160_000;

// [minimum video bitrate for the tier, longest-edge cap in pixels].
// Lower bitrates get smaller frames so quality-per-pixel stays watchable.
export const VIDEO_DIMENSION_TIERS: ReadonlyArray<readonly [number, number]> = [
  [800_000, 1280],
  [400_000, 854],
  [0, 640],
];

// WhatsApp Cloud API (WAPI) media types Meta accepts, verbatim from its docs.
// Anything else sent on WAPI is converted client-side first, even when it is
// already under the byte limit — Meta rejects it with 3026 / wba-10015.
// INT-1683: no image/webp. Meta takes WebP only as a sticker; as an image it
// answers 131053 "WebP image uploads are not currently supported".
export const WAPI_ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png'];
export const WAPI_ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/3gpp'];
// Meta rejects PNGs above 8 bits per channel ("8 bit/channel") even though
// the mimetype is on its allow-list.
export const PNG_MAX_ACCEPTED_BIT_DEPTH = 8;
// PNG signature (8) + IHDR length (4) + "IHDR" (4) + width (4) + height (4) +
// bit depth (1): the bytes readPngBitDepth needs.
export const PNG_HEADER_BYTES = 25;
// INT-1683: the bytes read from every WAPI image before deciding whether to
// convert it. Covers the PNG header above and a WebP's VP8X flags (byte 20),
// and matches what whatsapp-business-service reads before the send.
export const IMAGE_HEADER_BYTES = 30;

// The only bitrates WebCodecs' AAC encoder accepts (mediabunny snaps to the
// same list); any other value makes the encode throw. When the remux has to
// re-encode audio (primed AAC, Opus) it takes the lowest one that does not
// fall below the source, instead of mediabunny's 192 kbps default.
export const AAC_ENCODER_BITRATES_BPS = [96_000, 128_000, 160_000, 192_000];
// H.264 needs roughly twice HEVC's bitrate for the same picture; a remux that
// transcodes is capped at this multiple of the source's bitrate.
export const REMUX_AVC_BITRATE_FACTOR = 2;
